import React, { useState, useEffect, forwardRef, useRef } from 'react';
import { useParams } from "react-router";
import { useVideoJS } from "../hooks/useVideoJS";
import { Hosts, Theta } from "../constants";
import HlsJs from "hls.js";
import videojs from "video.js";
import useTVA from "../hooks/useTVA";

// Theta Plugins
const ThetaHlsPlugin = require("../theta/theta-hls-plugin.cjs.js").default;
const ThetaVideoJSPlugin = require("../theta/videojs-theta-plugin.cjs.js").default;
const ThetaVideoMetricsPlugin = require("../theta/videojs-theta-video-metrics-plugin.cjs.js").default;
const TVA = require("../theta/tva.cjs.js").TVA;
console.log(TVA);

// Monkey Patch XHRHttpRequest
(function () {
    var xhrProto = XMLHttpRequest.prototype,
        origOpen = xhrProto.open;

    xhrProto.open = function (method, url) {
        if (url.indexOf(`${Hosts.API}/key/video_`) === 0) {
            this.withCredentials = true;
        }
        return origOpen.apply(this, arguments);
    };
})();

const checkAccess = async (videoId, signin) => {
    const headers = {};
    if (signin) {
        headers['x-theta-drm-signin'] = JSON.stringify(signin);
    }

    const response = await fetch(
        `${Hosts.API}/key/${videoId}`,
        {
            headers: headers,
            credentials: 'include'
        }
    );

    if (response.ok) {
        let jsonResponse;
        try {
            jsonResponse = await response.json();
            return [false, jsonResponse.message]
        } catch (e) {
            // key returned, not JSON
            return [true, ''];
        }
    } else { 
        // Backend returns 401 if access is denied
        let jsonResponse;
        try {
            jsonResponse = await response.json();
            return [false, jsonResponse.message]
        } catch (e) {
            return [false, ''];
        }
    }
};

function LoginPanel({ videoId, drmRules }) {
    const [err, setErr] = useState('');

    const onLoginClick = async () => {
        if (!ethereum) {
            return;
        }

        setErr('');

        await ethereum.request({ method: 'eth_requestAccounts' });
        const accounts = await ethereum.request({ method: 'eth_accounts' });
        const from = accounts[0];
        const timestamp = (new Date()).getTime();

        const msgParams = JSON.stringify(
            {
                domain: {
                    chainId: 361, // Should always be Theta mainnet now
                    name: 'Theta DRM',
                    version: '1',
                },
                message: {
                    contents: 'Theta DRM Sign In',
                    timestamp: timestamp
                },
                // Refers to the keys of the *types* object below.
                primaryType: 'SignIn',
                types: {
                    EIP712Domain: [
                        { name: 'name', type: 'string' },
                        { name: 'version', type: 'string' },
                        { name: 'chainId', type: 'uint256' },
                    ],
                    // Refer to PrimaryType
                    SignIn: [
                        { name: 'timestamp', type: 'uint256' },
                        { name: 'contents', type: 'string' },
                    ]
                },
            });

        var params = [from, msgParams];
        var method = 'eth_signTypedData_v4';

        if (+ethereum.networkVersion !== 361) {
            setErr("Please change your Metamask network to Theta Mainnet");
            return;
        }

        let sig;
        
        try {
            sig = await ethereum.request(
                {
                    method,
                    params,
                    from,
                }
            );
        } catch(e) {
            let msg = e['message'] || 'Metamask returned error';
            setErr(msg);
            return;
        }


        const signin = {
            address: from,
            timestamp: timestamp,
            sig: sig
        };

        const [hasAccess, errMsg] = await checkAccess(videoId, signin);

        if (hasAccess) {
            // Reload the page.
            location.reload();
        } else {
            setErr(errMsg);
        }
    }


    return (<div className={'DRMPanel'}>
        <div className={'DRMPanel__title'}>Theta NFT-based DRM Digital Rights Management&#8482;</div>
        <div className={'DRMPanel__subtitle'}>This video is only available to NFT owners in collection:</div>
        <div className={'DRMPanel__subtitle'}>{drmRules && drmRules.map(rule => rule.nft_collection).join(", ")}</div>
        <div className={'DRMPanel__description'}>To unlock this video, please connect your wallet</div>
        <button className={'Button Button--green Button--medium'} onClick={onLoginClick}>Connect Wallet</button>
        { err && <div className={'DRMPanel__error'}><div>{err}</div></div> }
    </div >);
}

function StudioDRMVideoPlayer({ video }) {
    const tvaRef = useRef(null);
    const { Video, tva, errorMessage, isAccessBlocked } = useTVA(tvaRef, video.id, '');

    return (
        <Video muted loop />
    );
}

function VideoPlayer({ video }) {
    const playbackUrl = video?.playback_uri;
    const playerRef = useRef(null);
    const { Video, player, ready } = useVideoJS(
        {
            controls: true,
            playsinline: true,
            techOrder: ["theta_hlsjs", "html5"],
            sources: [{
                src: playbackUrl,
                type: "application/vnd.apple.mpegurl",
                label: "auto"
            }],
            theta_hlsjs: {
                videoId: video.id,
                userId: null,
                walletUrl: "wss://api-wallet-service.thetatoken.org/theta/ws",
                onWalletAccessToken: async () => {
                    return null
                },
                onStreamReady: () => {
                    // playerRef.current.thetaVideoMetrics({
                    //   partner_id: 'cool',
                    //   video_id: video.id,
                    //   user_id: 'someone'
                    // });

                    setTimeout(function () {
                        try {
                            const promise = playerRef.current.autoplay('any');

                            if (_.isNil(promise)) {
                                const playButton = playerRef.current.getChild('bigPlayButton');
                                playerRef.current.autoplay('muted');
                                playButton.show();
                            }
                        }
                        catch (e) {
                            const playButton = playerRef.current.getChild('bigPlayButton');
                            playerRef.current.autoplay('muted');
                            playButton.show();
                        }
                    }, 10);
                },
            },
            video_id: video.id,
            partner_id: null,
        },
        '',
        playerRef
    );


    return (
        <Video muted loop />
    );
}

function VideoPage() {
    const { videoId } = useParams();
    const [video, setVideo] = useState(null);
    const [hasAccess, setHasAccess] = useState(true);
    const isStream = videoId.startsWith('stream_');

    const fetchVideo = async (videoId) => {
        const response = await fetch(
            isStream ? `${Hosts.API}/stream/${videoId}` : `${Hosts.API}/video/${videoId}`,
            {credentials: "include"}
        );
        const jsonResponse = await response.json();

        if (response.ok) {
            const video = _.get(jsonResponse, isStream ? 'body' : 'body.videos.0');
            console.log('video == ', video);
            setVideo(video);

            if (video.use_drm) {
              const [hasAccess] = await checkAccess(videoId);
              setHasAccess(hasAccess);
            }
        }
        else {
            // TODO try again
        }
    };



    useEffect(() => {
        ThetaHlsPlugin.registerPlugin(window.Theta, HlsJs);
        ThetaVideoJSPlugin.registerPlugin(window.Theta, HlsJs, videojs);
    });

    useEffect(() => {
        (async () => {
            if (videoId) {
                await fetchVideo(videoId);

                if (video && video['use_drm']) {
                    await checkAccess(videoId);
                }
            }
        })();
    }, [videoId]);

    let overlay = (<></>);
    if (video) {
        if (hasAccess) {
            overlay = (<></>);
        } else {
            overlay = (<LoginPanel videoId={videoId} drmRules={video['drm_rules']} />);
        }
    }

    return (
        <div className={'VideoPage'}>
            {overlay}
            {video && !video.use_studio_drm && <VideoPlayer video={video}
            />}
            {video && video.use_studio_drm && <StudioDRMVideoPlayer video={video}
            />}
        </div>
    );
}


export default VideoPage;
